import { type Editor, Range } from 'slate';
import type {
  AnnotationsProvider,
  DecorateNodeCallback,
  EditorPlugin,
  Decorations,
} from '../../types/aha-editor';
import DecorationUtils from '../../AhaEditorUtils/DecorationUtils';
import Annotations from './Annotations';
import AhaEditor from '../../AhaEditor';

export function decorateAnnotations(
  editor: Editor,
  decorateNode: DecorateNodeCallback
): void {
  if (!editor.metadata?.annotations) return;

  const selectedAnnotationIds = Annotations.getAnnotationIdsForHighlighting(
    editor,
    editor.selection
  );
  const annotations = Object.values(editor.metadata.annotations).filter(
    Boolean
  );
  for (const annotation of annotations) {
    if (!annotation) continue;
    if (annotation.kind === 'comment' && annotation.resolved) continue;

    const fullAnnotationRange = annotation.range.current;
    if (!fullAnnotationRange || Range.isCollapsed(fullAnnotationRange)) {
      continue;
    }

    const fieldClassName = DecorationUtils.decorationClass(
      'aha-editor-field-v2__annotation'
    );
    const focusedClassName = DecorationUtils.decorationClass(
      'aha-editor-field-v2__annotation--focused'
    );
    const typeClassName = DecorationUtils.decorationClass(
      `aha-editor-field-v2__annotation--${annotation.kind}`
    );

    const decorationsObject: Decorations = {
      classNames: {
        [typeClassName]: annotation.kind !== 'comment' || !annotation.resolved,
        [fieldClassName]: !(
          annotation.kind === 'comment' && annotation.resolved
        ),
        [focusedClassName]:
          (annotation.kind === 'comment' &&
            !annotation.resolved &&
            selectedAnnotationIds.includes(annotation.key)) ||
          (annotation.kind !== 'comment' &&
            selectedAnnotationIds.includes(annotation.key)),
      },
      handlers: {
        onClick: (event: MouseEvent) => {
          AhaEditor.withProvider(
            editor,
            'annotations',
            (provider: AnnotationsProvider) => {
              provider.onAnnotationClicked(annotation.key);
            }
          );
        },
      },
    };

    DecorationUtils.decorateTextNodesInRange(
      editor,
      fullAnnotationRange,
      decorationsObject,
      decorateNode
    );
  }
}

const AnnotationsDecorator: EditorPlugin = {
  name: 'Annotations decorator plugin',
  decorator: {
    decorate: decorateAnnotations,
  },
};

export default AnnotationsDecorator;
